import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import './Switch.css';

/**
 * Switch — 켜고 끄는 **설정** (디자인 정본 B7).
 *
 * ★★★ **Switch 와 Checkbox 는 «언제 반영되느냐»로 가른다.**
 *    Switch = **누르는 즉시** 반영되는 설정. Checkbox = **저장 버튼을 눌러야** 반영되는 값.
 *    이 규칙이 없으면 사용자가 매번 「저장을 눌러야 하나?」를 묻게 된다.
 *    즉시 반영이 아닌 자리에 스위치를 쓰면 **껐는데 안 꺼진 것처럼** 보인다.
 *
 * ★★ **라벨에는 상태가 아니라 «대상»을 적는다** — 「알림 켜짐」 ✗ → 「알림」 ✓.
 *    상태는 스위치 그림과 `aria-checked` 가 말한다. 라벨까지 상태를 말하면
 *    껐을 때 「알림 켜짐(꺼짐)」 같은 문장이 된다.
 *
 * ★ `<button role="switch">` 로 만든다 — 체크박스가 아니라 **즉시 동작하는 단추**라서다.
 *   폼에 실려 제출되는 값이면 그건 Checkbox 다.
 */
export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'type' | 'value'> {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  /** 무엇을 켜고 끄는지. 상태를 적지 않는다. */
  label?: ReactNode;
  /** 라벨을 안 쓸 때의 이름 — 둘 중 하나는 반드시 있어야 한다. */
  'aria-label'?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, className, disabled, onClick, ...rest },
  ref,
) {
  return (
    <label className={['wc-switch', disabled ? 'wc-switch--disabled' : '', className ?? ''].filter(Boolean).join(' ')}>
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        className="wc-switch__track"
        disabled={disabled}
        onClick={(e) => {
          onClick?.(e);
          if (!e.defaultPrevented) onCheckedChange(!checked);
        }}
        {...rest}
      >
        {/* ⚠ 손잡이(thumb)를 빼면 스위치가 «아예 안 보인다» — 지금 화면의 `.svc-switch` 가 그랬다. */}
        <span className="wc-switch__thumb" aria-hidden />
      </button>
      {label != null && <span className="wc-switch__label">{label}</span>}
    </label>
  );
});
